import React from 'react';
import './RetirementResultsSummary.css';

interface RetirementResultsSummaryProps {
  results: any;
  formatCurrency: (value: number) => string;
}

const RetirementResultsSummary: React.FC<RetirementResultsSummaryProps> = ({
  results,
  formatCurrency
}) => {
  if (!results || !results.percentiles) return null;

  const { percentiles, distribution_stats, suggested_oas_age, suggested_cpp_age } = results;
  const median_estate = percentiles.estate_value.p50;
  const median_death_age = percentiles.death_age.p50;
  const ran_out_prob = distribution_stats.ran_out_probability;

  // Determine health of plan
  const getPlanHealth = () => {
    if (ran_out_prob >= 0.5) return { status: 'critical', label: 'High Risk', icon: '⚠️' };
    if (ran_out_prob >= 0.2) return { status: 'warning', label: 'Moderate Risk', icon: '⚡' };
    if (ran_out_prob >= 0.05) return { status: 'good', label: 'Low Risk', icon: '✓' };
    return { status: 'excellent', label: 'Very Safe', icon: '🎯' };
  };

  const health = getPlanHealth();

  return (
    <div className="retirement-summary-container">
      <div className="summary-header">
        <h2>Your Optimized Retirement Strategy</h2>
        <div className={`plan-health ${health.status}`}>
          <span className="health-icon">{health.icon}</span>
          <span className="health-label">{health.label}</span>
        </div>
      </div>

      <div className="summary-grid">
        {/* Recommended Strategy */}
        <div className="summary-card primary">
          <div className="card-header">
            <span className="card-icon">🎯</span>
            <h3>Recommended Strategy</h3>
          </div>
          <div className="card-content">
            <div className="strategy-item">
              <span className="strategy-label">Start OAS at Age</span>
              <span className="strategy-value highlight">{suggested_oas_age}</span>
            </div>
            <div className="strategy-item">
              <span className="strategy-label">Start CPP at Age</span>
              <span className="strategy-value highlight">{suggested_cpp_age}</span>
            </div>
            <div className="strategy-note">
              {suggested_oas_age > 65 && (
                <p>💡 Delaying OAS to {suggested_oas_age} gives you a {((suggested_oas_age - 65) * 12 * 0.6).toFixed(1)}% bonus</p>
              )}
            </div>
          </div>
        </div>

        {/* Expected Outcomes */}
        <div className="summary-card">
          <div className="card-header">
            <span className="card-icon">💰</span>
            <h3>Ending Balance (Estate Value)</h3>
          </div>
          <div className="card-content">
            <div className="percentile-range">
              <div className="range-bar">
                <div className="range-marker p25" style={{left: '25%'}}>
                  <span className="marker-label">25%</span>
                  <span className="marker-value">{formatCurrency(percentiles.estate_value.p25)}</span>
                </div>
                <div className="range-marker p50" style={{left: '50%'}}>
                  <span className="marker-label">Median</span>
                  <span className="marker-value primary">{formatCurrency(median_estate)}</span>
                </div>
                <div className="range-marker p75" style={{left: '75%'}}>
                  <span className="marker-label">75%</span>
                  <span className="marker-value">{formatCurrency(percentiles.estate_value.p75)}</span>
                </div>
              </div>
            </div>
            <p className="range-explanation">
              Half of outcomes between {formatCurrency(percentiles.estate_value.p25)} - {formatCurrency(percentiles.estate_value.p75)}
            </p>
          </div>
        </div>

        {/* Total Lifetime Tax */}
        {percentiles.lifetime_taxes && (
          <div className="summary-card">
            <div className="card-header">
              <span className="card-icon">🧾</span>
              <h3>Total Lifetime Tax Paid</h3>
            </div>
            <div className="card-content">
              <div className="metric-large">
                <span className="metric-value" style={{ fontSize: '28px' }}>{formatCurrency(percentiles.lifetime_taxes.p50)}</span>
                <span className="metric-label">Median</span>
              </div>
              <p className="range-explanation">
                Half of outcomes between {formatCurrency(percentiles.lifetime_taxes.p25)} - {formatCurrency(percentiles.lifetime_taxes.p75)}
              </p>
            </div>
          </div>
        )}

        {/* Life Expectancy */}
        <div className="summary-card">
          <div className="card-header">
            <span className="card-icon">📅</span>
            <h3>Life Expectancy</h3>
          </div>
          <div className="card-content">
            <div className="metric-large">
              <span className="metric-value">{median_death_age}</span>
              <span className="metric-label">Median Age</span>
            </div>
            <div className="metric-range">
              <span>25th: {percentiles.death_age.p25}</span>
              <span>75th: {percentiles.death_age.p75}</span>
            </div>
          </div>
        </div>

        {/* Plan Reliability */}
        <div className="summary-card">
          <div className="card-header">
            <span className="card-icon">📊</span>
            <h3>Plan Reliability</h3>
          </div>
          <div className="card-content">
            <div className="reliability-gauge">
              <div className="gauge-fill" style={{width: `${(1 - ran_out_prob) * 100}%`}}></div>
              <div className="gauge-label">{((1 - ran_out_prob) * 100).toFixed(0)}%</div>
            </div>
            <p className="reliability-text">
              Money lasts until death in <strong>{((1 - ran_out_prob) * 100).toFixed(0)}%</strong> of scenarios
            </p>
            {ran_out_prob > 0 && (
              <p className="warning-text" title="Average over the scenarios where the money ran out before death">
                ⚠️ {(ran_out_prob * 100).toFixed(0)}% risk of running out
                {distribution_stats.avg_ran_out_age ? `, on average at age ${Math.round(distribution_stats.avg_ran_out_age)}` : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Key Insights */}
      <div className="key-insights">
        <h3>💡 Key Insights</h3>
        <ul>
          {suggested_oas_age > 65 && (
            <li>Delaying OAS maximizes your lifetime government benefits</li>
          )}
          {suggested_cpp_age < 65 && (
            <li>Taking CPP early provides income bridge before OAS starts</li>
          )}
          {ran_out_prob === 0 && (
            <li>Your plan is very robust - money lasted in all {distribution_stats.num_scenarios.toLocaleString()} scenarios tested</li>
          )}
          {median_estate > 1000000 && (
            <li>Significant estate expected - consider tax-efficient wealth transfer strategies</li>
          )}
        </ul>
      </div>

      {/* Recommendations for High Risk Plans */}
      {ran_out_prob >= 0.2 && (
        <div className="recommendations-warning">
          <h3>⚠️ Recommended Actions to Reduce Risk</h3>
          <p>Your current plan has a {(ran_out_prob * 100).toFixed(0)}% chance of running out of money. Consider these options:</p>
          <ul>
            <li><strong>Reduce Spending:</strong> Lowering annual spending by 10-20% significantly improves plan success</li>
            <li><strong>Work Longer:</strong> Even 1-2 more years of work builds savings and reduces retirement duration</li>
            <li><strong>Delay Government Benefits:</strong> Starting CPP/OAS at 70 instead of 65 increases payments by 42%</li>
            <li><strong>Increase Returns:</strong> Review investment allocation - higher growth potential may be appropriate</li>
          </ul>
        </div>
      )}

      {ran_out_prob >= 0.5 && median_estate < 0 && (
        <div className="recommendations-critical">
          <h3>🚨 Critical: Plan Likely to Fail</h3>
          <p>In the median scenario, you run out of money before death. Immediate action required:</p>
          <ul>
            <li><strong>Reduce Spending Significantly:</strong> Try reducing annual spending by 15-25% and re-run the analysis</li>
            <li><strong>Delay Retirement:</strong> Work 2-3 more years to build additional savings</li>
            <li><strong>Maximize CPP/OAS:</strong> Delay both to age 70 for maximum benefits (up to 42% higher)</li>
            <li><strong>Consider Part-Time Work:</strong> Even $10-15k/year in early retirement helps significantly</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default RetirementResultsSummary;
